import React, { useEffect } from "react";
import { Stack, Alert, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";

import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { resetUserPassword } from "../redux/actions/authActions";

const resetPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Password confirmation is required"),
});

interface ResetPasswordFormProps {
  token: string;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ token }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { loadingResetPassword, errorResetPassword, serverStatus } =
    useAppSelector((state) => state.auth);

  useEffect(() => {
    if (
      serverStatus?.operation === "reset-password" &&
      serverStatus?.status === 200
    ) {
      const timer = setTimeout(() => navigate("/auth"), 3000);
      return () => clearTimeout(timer);
    }
  }, [serverStatus, navigate]);

  return (
    <Box sx={{ width: "100%" }}>
      <Formik
        initialValues={{ newPassword: "", confirmPassword: "" }}
        validationSchema={resetPasswordSchema}
        onSubmit={(values) => {
          dispatch(
            resetUserPassword({
              token,
              newPassword: values.newPassword.trim(),
            }),
          );
        }}
      >
        {({
          isValid,
        }: FormikProps<{ newPassword: ""; confirmPassword: "" }>) => (
          <Form>
            <Stack spacing={2}>
              {errorResetPassword && (
                <Alert severity="error" sx={{ textAlign: "left" }}>
                  {errorResetPassword}
                </Alert>
              )}

              {serverStatus?.operation === "reset-password" && (
                <Alert severity="success" sx={{ textAlign: "left" }}>
                  Password updated! Redirecting to login...
                </Alert>
              )}

              <FormTextField
                name="newPassword"
                label="New Password"
                type="password"
                slotProps={{
                  input: {
                    startAdornment: (
                      <LockOutlinedIcon
                        sx={{ color: "action.active", mr: 1 }}
                      />
                    ),
                  },
                }}
              />

              <FormTextField
                name="confirmPassword"
                label="Confirm New Password"
                type="password"
                slotProps={{
                  input: {
                    startAdornment: (
                      <LockOutlinedIcon
                        sx={{ color: "action.active", mr: 1 }}
                      />
                    ),
                  },
                }}
              />

              <SubmitButton
                loading={loadingResetPassword}
                title="Update Password"
                loadingText="Updating..."
                disabled={
                  !isValid ||
                  serverStatus?.status === 200 ||
                  loadingResetPassword
                }
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ResetPasswordForm;
